import { useMemo, useState } from 'react'
import { Check, Search, Users } from 'lucide-react'
import Checkbox from './Checkbox'
import SegmentedControl from './SegmentedControl'
import { kit as K } from './strings'
import './GroupPicker.css'

/**
 * A checkbox list of departments, with select-all and a filter.
 *
 * Four legacy forms assign something to departments — the campaign, the
 * bulletin, the survey and the policy — and each does it with a select2 whose
 * "select all" is a sentinel OPTION. An option that is not a value is a trap:
 * it shows as a chosen item and posts nothing. Here select-all is a control,
 * and the filter narrows the list without changing what is selected.
 *
 * BUILT FOR A DIRECTORY, not a handful. An installation can carry thousands of
 * departments, which rules out listing the chosen ones back: a selection of four
 * hundred is not something to render as chips, and at that size the only summary
 * worth having is how many of how many. Only a window of matches goes into the
 * DOM, with the rest named rather than silently dropped.
 *
 * MORE THAN ONE KIND OF GROUP. Pass `kinds` — `[{ id, label }]` — and give each
 * option the `kind` it belongs to, and the list gets a switch between them. A
 * form that can reach departments AND special groups needs it: the departments
 * come first and can fill the whole window, so without it the special groups
 * sat past the two hundredth row where nobody would find them.
 *
 * DRAWN AS NAME CHIPS (2026-10-19, the client's request for a cleaner box). The
 * departments sit on an even grid of soft tiles, four across a form, so a list of
 * thirty is seen whole and reads down straight columns; each is still a real
 * checkbox inside its label. A ring means not chosen and a filled check means
 * chosen — the same mark the filter cards use. The count sits beside the label,
 * as "3 of 31".
 */
const WINDOW = 200
const ALL_KINDS = 'all'

export default function GroupPicker({
  options = [],
  selected = [],
  onChange,
  legend,
  error,
  selectAllLabel,
  filterLabel,
  emptyLabel = K.pickerEmpty,
  moreLabel = K.pickerMore,
  kinds = [],
}) {
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState(ALL_KINDS)

  /* Only the kinds this list actually holds — a switch with one real choice in it
     is a control that does nothing. */
  const presentKinds = useMemo(
    () => kinds.filter((item) => options.some((option) => option.kind === item.id)),
    [kinds, options],
  )
  const switchable = presentKinds.length > 1
  // A kind the options no longer hold would show an empty list; fall back to all.
  const activeKind = presentKinds.some((item) => item.id === kind) ? kind : ALL_KINDS

  /* MATCHED BY KEY, NOT BY IDENTITY. The options carry whatever the endpoint
     sent — `GroupId` is a number — and a caller holding its ids as strings is
     the normal case, since a form that round-trips them through a URL or a
     `<select>` has strings by the time they come back. Comparing raw, every
     such caller drew a tally reading "3 / 40" over forty empty checkboxes: the
     count is the length of the array, which was right, and the ticks were a Set
     lookup of '3' against 3, which never hit. One key function decides
     membership everywhere below; what is EMITTED stays the option's own value,
     so callers keep getting back the types they gave. */
  const keyOf = (value) => String(value)

  /* A Set, not `includes`: at four thousand departments the linear scan runs
     once per row on every keystroke. */
  const chosen = useMemo(() => new Set(selected.map(keyOf)), [selected])

  const matches = useMemo(() => {
    const ofKind =
      switchable && activeKind !== ALL_KINDS
        ? options.filter((option) => option.kind === activeKind)
        : options
    const needle = query.trim().toLowerCase()
    if (!needle) return ofKind
    return ofKind.filter((option) => String(option.label).toLowerCase().includes(needle))
  }, [activeKind, options, query, switchable])

  const shown = matches.length > WINDOW ? matches.slice(0, WINDOW) : matches

  /* Select-all applies to what is MATCHED, not to what is on screen: the window
     is a rendering limit, and "all" meaning the first two hundred would be a
     quiet lie. With a filter up it is still the filtered set, not the directory. */
  const allMatchedSelected = matches.length > 0 && matches.every((o) => chosen.has(keyOf(o.value)))
  const someMatchedSelected = !allMatchedSelected && matches.some((o) => chosen.has(keyOf(o.value)))

  const toggleAll = () => {
    const matched = new Set(matches.map((option) => keyOf(option.value)))
    onChange(
      allMatchedSelected
        ? selected.filter((id) => !matched.has(keyOf(id)))
        : /* Keeps what is already chosen — including anything the filter is
             currently hiding — and appends only the matches that are not. */
          [...selected, ...matches.filter((option) => !chosen.has(keyOf(option.value))).map((option) => option.value)],
    )
  }

  const toggle = (value) =>
    onChange(
      chosen.has(keyOf(value))
        ? selected.filter((item) => keyOf(item) !== keyOf(value))
        : [...selected, value],
    )

  const count = (value) => value.toLocaleString('en-US')

  return (
    <fieldset className="group-picker">
      <legend className="group-picker__legend">
        <span className="group-picker__title">
          <bdi>{legend}</bdi>
          {error && <span className="group-picker__error">{error}</span>}
        </span>
        {/* THE SUMMARY, and at this scale the only one worth having: how many of how
            many. Beside the label, where a field keeps its counter. */}
        <span className={`group-picker__count${selected.length ? ' has-some' : ''}`}>
          <strong className="tabular">{count(selected.length)}</strong>
          <span className="tabular">{K.pickerOf(count(options.length))}</span>
        </span>
      </legend>

      <div className={`group-picker__box${error ? ' is-invalid' : ''}`}>
        <div className="group-picker__head">
          <Checkbox checked={allMatchedSelected} indeterminate={someMatchedSelected} onChange={toggleAll}>
            {selectAllLabel}
          </Checkbox>

          <span className="group-picker__search">
            <Search size={15} strokeWidth={1.75} aria-hidden="true" />
            <input
              type="search"
              value={query}
              placeholder={filterLabel}
              aria-label={filterLabel}
              onChange={(event) => setQuery(event.target.value)}
            />
          </span>
        </div>

        {/* Narrows the list like the filter does: what is chosen stays chosen. */}
        {switchable && (
          <div className="group-picker__kinds">
            <SegmentedControl
              variant="underline"
              ariaLabel={K.pickerKinds}
              value={activeKind}
              onChange={setKind}
              items={[
                { id: ALL_KINDS, label: K.pickerAll, count: options.length },
                ...presentKinds.map((item) => ({
                  id: item.id,
                  label: item.label,
                  count: options.filter((option) => option.kind === item.id).length,
                })),
              ]}
            />
          </div>
        )}

        {shown.length === 0 ? (
          <p className="group-picker__empty">
            <Users size={16} strokeWidth={1.75} aria-hidden="true" />
            <bdi>{emptyLabel}</bdi>
          </p>
        ) : (
          <>
            <div className="group-picker__chips">
              {shown.map((option) => {
                const on = chosen.has(keyOf(option.value))
                return (
                  /* A real checkbox inside its label: the label is the chip, so a click
                     anywhere on it ticks the box and the name stays the box's name. */
                  <label
                    key={option.value}
                    className={`group-picker__chip${on ? ' is-on' : ''}`}
                    title={String(option.label)}
                  >
                    <input
                      type="checkbox"
                      className="group-picker__input"
                      checked={on}
                      onChange={() => toggle(option.value)}
                    />
                    <span className="group-picker__mark" aria-hidden="true">
                      <Check size={10} strokeWidth={3.5} />
                    </span>
                    <bdi className="group-picker__name">{option.label}</bdi>
                  </label>
                )
              })}
            </div>

            {/* Named, not dropped: a list that quietly stops at two hundred looks
                like a directory that ends there. */}
            {matches.length > shown.length && (
              <p className="group-picker__more">
                {moreLabel(count(shown.length), count(matches.length))}
              </p>
            )}
          </>
        )}
      </div>
    </fieldset>
  )
}
